import { useEffect, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { AnimatePresence, motion } from 'framer-motion';
import confetti from 'canvas-confetti';
import {
  leaderboardApi,
  type LeaderboardRow,
  type LeaderboardSnapshot,
} from '@/api/leaderboard';
import { tournamentApi } from '@/api/tournament';
import { useLeaderboardStream } from '@/hooks/useLeaderboardStream';
import { useSession } from '@/state/session';
import { LedNumber } from '@/components/LedNumber';
import { SignPanel } from '@/components/SignPanel';
import { cn } from '@/lib/utils';

/**
 * Live leaderboard for the active tournament. Reads the active-tournament
 * endpoint just to discover the ID, then subscribes to the SSE stream and
 * the snapshot query.
 */
export function Leaderboard() {
  // Discover the active tournament. (If none, render an empty state.)
  const activeQ = useQuery({
    queryKey: ['tournament', 'active'],
    queryFn: tournamentApi.active,
    staleTime: 30_000,
  });
  const tid = activeQ.data?.tournament?.id ?? null;

  return tid ? <Live tid={tid} /> : <NoActive loading={activeQ.isLoading} />;
}

function NoActive({ loading }: { loading: boolean }) {
  return (
    <main className="mx-auto max-w-md px-5 py-12 text-center space-y-3">
      <h1 className="font-display text-cabinet-gold text-2xl uppercase tracking-widest">
        Leaderboard
      </h1>
      <p className="text-cabinet-cream/70 text-sm">
        {loading ? 'Loading…' : 'No tournament running right now.'}
      </p>
      {!loading && (
        <Link to="/" className="inline-block text-cabinet-gold underline-offset-2 hover:underline text-sm">
          ← Back home
        </Link>
      )}
    </main>
  );
}

function Live({ tid }: { tid: number }) {
  useLeaderboardStream(tid);
  const user = useSession((s) => s.user);
  const myUserId = user?.id ?? null;

  const q = useQuery({
    queryKey: ['leaderboard', tid],
    queryFn: () => leaderboardApi.get(tid),
    refetchOnMount: 'always',
  });

  // Fire confetti exactly once per (entryId × verification) when MY row
  // appears in a paying position. localStorage keeps the dedupe across
  // page reloads + reconnects.
  useConfettiOnPaying(q.data, myUserId);

  if (q.isLoading || !q.data) {
    return (
      <main className="mx-auto max-w-md px-5 py-12 text-center">
        <p className="text-cabinet-cream/60">Loading…</p>
      </main>
    );
  }
  return <Inner snap={q.data} myUserId={myUserId} />;
}

function Inner({
  snap,
  myUserId,
}: {
  snap: LeaderboardSnapshot;
  myUserId: number | null;
}) {
  const { tournament, ranked, pending, potCents } = snap;

  return (
    <main className="mx-auto max-w-2xl px-5 py-6 space-y-5">
      <header className="space-y-1">
        <p className="font-display uppercase tracking-widest text-cabinet-gold-bright text-xs">
          {tournament.machineName}
        </p>
        <SignPanel size="md">
          <h1 className="text-marquee text-3xl uppercase leading-none">
            {tournament.name}
          </h1>
        </SignPanel>
        <div className="mt-2 flex items-center justify-between">
          <span className="text-xs uppercase tracking-widest text-cabinet-cream/60">
            Pot
          </span>
          <LedNumber value={Math.round(potCents / 100)} currency digits={5} size="md" />
        </div>
      </header>

      <section className="cabinet-card p-4 sm:p-5 space-y-3">
        <h2 className="font-display text-cabinet-gold text-lg uppercase tracking-widest">
          Leaderboard
        </h2>
        {ranked.length === 0 ? (
          <p className="text-sm text-cabinet-cream/60 text-center py-6">
            No verified entries yet — be the first!
          </p>
        ) : (
          <ol className="space-y-2">
            <AnimatePresence initial={false}>
              {ranked.map((row) => (
                <motion.li
                  key={row.userId}
                  layout
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  transition={{ type: 'spring', stiffness: 320, damping: 28 }}
                >
                  <Row row={row} isMe={row.userId === myUserId} />
                </motion.li>
              ))}
            </AnimatePresence>
          </ol>
        )}
      </section>

      {pending.length > 0 && (
        <section className="cabinet-card p-4 sm:p-5 space-y-2 border-cabinet-flame-orange/30">
          <h3 className="font-display text-cabinet-flame-orange text-sm uppercase tracking-widest">
            Pending verification ({pending.length})
          </h3>
          <ul className="space-y-1.5">
            {pending.map((p) => (
              <li
                key={`${p.userId}-${p.entryNumber}`}
                className="flex items-center justify-between text-sm text-cabinet-cream/80 px-2 py-1.5 rounded-md bg-cabinet-purple-ink/30 ring-1 ring-cabinet-cream/10"
              >
                <span className="truncate">{p.displayName}</span>
                <span className="text-[10px] uppercase tracking-widest text-cabinet-flame-orange">
                  Awaiting admin
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}

      <Link
        to="/tournament/active"
        className="block text-center text-sm text-cabinet-cream/60 hover:text-cabinet-gold"
      >
        ← Back to dashboard
      </Link>
    </main>
  );
}

function Row({ row, isMe }: { row: LeaderboardRow; isMe: boolean }) {
  return (
    <div
      className={cn(
        'flex items-center gap-3 rounded-xl px-3 py-2.5 ring-1 transition',
        isMe
          ? 'ring-cabinet-gold bg-cabinet-purple-deep animate-led-pulse'
          : row.paying
            ? 'ring-cabinet-gold/40 bg-cabinet-purple-ink/60'
            : 'ring-cabinet-cream/10 bg-cabinet-purple-ink/60',
      )}
    >
      <RankBadge rank={row.rank} paying={row.paying} />
      <div className="min-w-0 flex-1">
        <p className="truncate font-display tracking-wide text-cabinet-cream text-base">
          {row.displayName}
          {isMe && <span className="ml-2 text-xs text-cabinet-gold-bright">(you)</span>}
        </p>
        {row.paying && (
          <p className="text-[10px] uppercase tracking-widest text-cabinet-gold-bright">
            Paying place
          </p>
        )}
      </div>
      <LedNumber value={row.score ?? 0} digits={4} size="sm" />
    </div>
  );
}

function RankBadge({ rank, paying }: { rank: number; paying: boolean }) {
  const style =
    rank === 1
      ? 'bg-gold-gradient text-cabinet-flame-ember'
      : rank === 2
        ? 'bg-chrome-bevel text-cabinet-purple-ink'
        : rank === 3
          ? 'bg-flame-gradient text-cabinet-cream'
          : paying
            ? 'bg-cabinet-purple text-cabinet-gold'
            : 'bg-cabinet-purple text-cabinet-cream/70';
  return (
    <div
      className={cn(
        'flex h-10 w-10 shrink-0 items-center justify-center rounded-full font-display text-lg ring-2 ring-black/30',
        style,
      )}
      aria-label={`Rank ${rank}`}
    >
      {rank}
    </div>
  );
}

const CONFETTI_KEY_PREFIX = 'slot-tournament:confetti-fired:';

/**
 * Fires canvas-confetti once per (entryId × verification) when MY row lands
 * in a paying position. Dedupes via localStorage so reconnects don't
 * re-fire. Won't fire on initial mount unless the row was already paying
 * AND we haven't recorded a fire for that entryId.
 *
 * Edge case: if my best verified entry changes (e.g., I add a 2nd entry
 * that beats my first), the new entryId yields a fresh fire — that's
 * actually the right behavior (it's a new win).
 */
function useConfettiOnPaying(
  snap: LeaderboardSnapshot | undefined,
  myUserId: number | null,
): void {
  // Stable across re-renders so we don't fire twice in a single mount.
  const firedInSession = useRef(new Set<number>());

  // Memoize the lookup so we only react on real changes.
  const myPayingRow = useMemo<LeaderboardRow | null>(() => {
    if (!snap || myUserId === null) return null;
    return snap.ranked.find((r) => r.userId === myUserId && r.paying) ?? null;
  }, [snap, myUserId]);

  useEffect(() => {
    if (!myPayingRow) return;
    const entryId = myPayingRow.entryId;
    if (entryId === null) return;
    if (firedInSession.current.has(entryId)) return;
    const key = `${CONFETTI_KEY_PREFIX}${entryId}`;
    try {
      if (window.localStorage.getItem(key) === '1') {
        firedInSession.current.add(entryId);
        return;
      }
    } catch {
      /* private browsing — fall through and just fire */
    }
    fireConfetti();
    firedInSession.current.add(entryId);
    try {
      window.localStorage.setItem(key, '1');
    } catch {
      /* ignore */
    }
  }, [myPayingRow]);
}

function fireConfetti(): void {
  const colors = ['#fbbf24', '#fcd34d', '#f97316', '#dc2626', '#fef3c7'];
  const opts = {
    colors,
    spread: 70,
    startVelocity: 35,
    ticks: 220,
  } as const;
  // Two bursts from each side gives a nice celebratory shape.
  confetti({ ...opts, particleCount: 90, origin: { x: 0.15, y: 0.85 } });
  confetti({ ...opts, particleCount: 90, origin: { x: 0.85, y: 0.85 } });
  // One big burst from the middle/top.
  setTimeout(() => {
    confetti({ ...opts, particleCount: 120, origin: { x: 0.5, y: 0.4 } });
  }, 180);
}

